import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import { Link } from 'react-router-dom';
import { config } from '../config';
import { getImageUrl } from '../utils/imageUrl';

const fallbackEquipment = {
  hero: {
    eyebrow: 'Astro Vino Observatory',
    title: 'Equipment, rigs & scopes',
    intro: 'A dedicated home for the practical Astro Vino setup: observatory notes, imaging rigs, camera trains, scope configurations, weather/safety tooling and software projects that support capture sessions.',
    image: '/uploads/equipment/observatory-overview.jpg'
  },
  rigs: [
    { id: 'deep-sky', title: 'Deep-sky imaging rig', tag: 'Main capture', description: 'A dedicated telescope, mount, camera and filter workflow for longer exposures, calibration frames and repeatable deep-sky sessions.', image: '/uploads/equipment/deepsky-rig-eq8-askar80phq.jpg', order: 0 },
    { id: 'lunar-planetary', title: 'Lunar / planetary rig', tag: 'High frame-rate', description: 'A separate capture style for the Moon and planets, focused on high-speed video, seeing conditions and comparison tests.', image: '', order: 1 },
    { id: 'widefield', title: 'Widefield / portable setup', tag: 'Large targets', description: 'A lighter rig for wider compositions, faster setup, framing trials and equipment experiments outside the main workflow.', image: '/uploads/equipment/widefield-rig-askar-sqa55.jpg', order: 2 }
  ],
  gallery: [
    { id: 'observatory-overview', title: 'Observatory overview', image: '/uploads/equipment/observatory-overview.jpg', description: 'The home base for Astro Vino capture sessions, equipment checks and observatory tooling.', order: 0 },
    { id: 'main-imaging-rig', title: 'Main imaging rig', image: '/uploads/equipment/deepsky-rig-eq8-askar80phq.jpg', description: 'Primary deep-sky setup for longer imaging runs and repeatable capture workflows.', order: 1 },
    { id: 'camera-train', title: 'Camera train detail', image: '/uploads/equipment/deepsky-camera-train.jpg', description: 'Camera, filter and spacing details for practical kit testing and future notes.', order: 2 },
    { id: 'widefield-rig', title: 'Widefield rig', image: '/uploads/equipment/widefield-rig-askar-sqa55.jpg', description: 'Compact widefield setup for larger targets, framing experiments and travel-friendly captures.', order: 3 },
    { id: 'rig-layout', title: 'Rig layout', image: '/uploads/equipment/observatory-rigs-wide.jpg', description: 'Multiple rigs and scopes arranged around the observatory workflow.', order: 4 },
    { id: 'control-detail', title: 'Control detail', image: '/uploads/equipment/observatory-control-detail.jpg', description: 'Weather, safety and control tooling that supports nights without overwhelming the front page.', order: 5 }
  ],
  systems: [
    { id: 'weather', title: 'Weather & sky condition tools', description: 'Tempest weather, all-sky condition checks and night-planning data inform whether a setup is worth opening for a session.', order: 0 },
    { id: 'safety', title: 'Safety and automation', description: 'ASCOM safety monitor work and observatory integrations help keep equipment decisions grounded in real conditions.', order: 1 },
    { id: 'software', title: 'Software projects', description: 'AstroVEdit, collimation tools and observatory utilities sit alongside the hardware so the full capture workflow is visible.', order: 2 }
  ]
};

const sortByOrder = (items = []) => [...items].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
const imageSrc = (value) => value ? getImageUrl(value) : '';

function Equipment() {
  const [equipment, setEquipment] = useState(fallbackEquipment);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    fetch(config.endpoints.equipment)
      .then((response) => {
        if (!response.ok) throw new Error('Could not load editable equipment data');
        return response.json();
      })
      .then((data) => {
        if (mounted) setEquipment({ ...fallbackEquipment, ...data });
      })
      .catch((err) => {
        console.error('Equipment load error:', err);
        if (mounted) setError('Showing fallback equipment details while the editable equipment data is unavailable.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const hero = equipment.hero || fallbackEquipment.hero;
  const rigs = sortByOrder(equipment.rigs?.length ? equipment.rigs : fallbackEquipment.rigs);
  const gallery = sortByOrder(equipment.gallery?.length ? equipment.gallery : fallbackEquipment.gallery);
  const systems = sortByOrder(equipment.systems?.length ? equipment.systems : fallbackEquipment.systems);

  return (
    <Box sx={{ background: 'radial-gradient(circle at 14% 0%, rgba(108,54,145,0.25), transparent 34%), radial-gradient(circle at 86% 8%, rgba(245,151,79,0.13), transparent 28%), #080910', minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="xl" sx={{ pt: { xs: 4, md: 6 } }}>
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
        <Paper sx={{ p: { xs: 2.2, md: 4 }, mb: 3, overflow: 'hidden', background: 'linear-gradient(135deg, rgba(255,255,255,0.075), rgba(108,54,145,0.16))', border: '1px solid rgba(245,151,79,0.18)' }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={1.5}>
                <Chip label={hero.eyebrow || fallbackEquipment.hero.eyebrow} sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(245,151,79,0.14)', color: 'primary.light', border: '1px solid rgba(245,151,79,0.34)' }} />
                <Typography variant="h1" sx={{ fontWeight: 900, letterSpacing: '-0.04em', fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.03 }}>
                  {hero.title || fallbackEquipment.hero.title}
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 760, lineHeight: 1.65, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>
                  {hero.intro || fallbackEquipment.hero.intro}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.3}>
                  <Button component={Link} to="/gallery" variant="contained" sx={{ bgcolor: 'primary.light', color: '#160920', fontWeight: 900, '&:hover': { bgcolor: '#ffb06f' } }}>
                    View captures
                  </Button>
                  <Button href="https://github.com/astro-vino" target="_blank" rel="noopener noreferrer" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.32)' }}>
                    Astro Vino code
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
              ) : (
                <Box component="img" src={imageSrc(hero.image || fallbackEquipment.hero.image)} alt="Astro Vino observatory overview" sx={{ width: '100%', maxHeight: 360, objectFit: 'cover', borderRadius: 3, border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 22px 70px rgba(0,0,0,0.38)' }} />
              )}
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2.2} sx={{ mb: 3 }}>
          {rigs.map((rig) => (
            <Grid item xs={12} md={4} key={rig.id || rig.title}>
              <Card sx={{ height: '100%', background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {rig.image && <CardMedia component="img" image={imageSrc(rig.image)} alt={rig.title} sx={{ height: 180, objectFit: 'contain', bgcolor: 'rgba(0,0,0,0.25)' }} />}
                <CardContent sx={{ p: 2.2 }}>
                  {rig.tag && <Chip size="small" label={rig.tag} sx={{ height: 22, mb: 1, color: 'primary.light' }} />}
                  <Typography variant="h6" sx={{ fontWeight: 850, fontSize: '1.05rem' }}>{rig.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>{rig.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Stack spacing={0.8} sx={{ mb: 2.2 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, fontSize: { xs: '1.25rem', md: '1.55rem' } }}>Rig and observatory gallery</Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 820 }}>Reference images for the equipment page. These are intentionally contained and captioned so they do not behave like clipped news thumbnails.</Typography>
          </Stack>
          <Grid container spacing={2}>
            {gallery.map((item) => (
              <Grid item xs={12} sm={6} lg={4} key={item.id || item.title}>
                <Card sx={{ height: '100%', background: 'rgba(3,8,18,0.72)', border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                  <CardMedia component="img" image={imageSrc(item.image)} alt={item.title} sx={{ height: 230, objectFit: 'contain', bgcolor: 'rgba(0,0,0,0.35)', borderBottom: '1px solid rgba(255,255,255,0.07)' }} />
                  <CardContent sx={{ p: 1.8 }}>
                    <Typography sx={{ fontWeight: 850 }}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8, lineHeight: 1.5 }}>{item.description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, background: 'linear-gradient(135deg, rgba(25,118,210,0.10), rgba(255,255,255,0.035))', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 1.5, fontSize: { xs: '1.2rem', md: '1.45rem' } }}>Control, weather and software stack</Typography>
          <Grid container spacing={2}>
            {systems.map((system) => (
              <Grid item xs={12} md={4} key={system.id || system.title}>
                <Stack spacing={1.1} sx={{ height: '100%', p: 2, borderRadius: 2, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <Typography sx={{ fontWeight: 850 }}>{system.title}</Typography>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{system.description}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default Equipment;
